import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';

import type { AcctStateType } from 'stores/atoms/acctState';
import type { ContentSearchItemType } from 'types/api/contentSearchResponseType';
import {
  PAGE_LIMIT,
  buildContentSearchPageUrl,
  computeOffset,
  parsePageNo,
} from '../utils/pagination';
import { dispatchContentCopy } from '../utils/contentCopy';
import { getContentsTypeStr, getContentsTypeStrJpn } from 'utils/content';

export interface UseWsContentSearchResult {
  initialized: boolean;
  processing: boolean;
  pageNoState: number;
  contents: ContentSearchItemType[];
  hasNext: boolean;
  modalCreateContentOpen: boolean;
  setModalCreateContentOpen: (open: boolean) => void;
  onModalCreateContentOpen: () => void;
  onPrev: () => void;
  onNext: () => void;
  onEdit: (content: ContentSearchItemType) => void;
  onReport: (content: ContentSearchItemType) => void;
  onCopy: (content: ContentSearchItemType) => void;
}

export function useWsContentSearch(
  pageNoParam: string | undefined,
  acctState: AcctStateType
): UseWsContentSearchResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { postContentSearch, postQuizCopy, postDiagnosisCopy, postFormCopy, postVoiceChatCopy } =
    useApiState();
  const { noticeMessageSuccess, confirm } = useAppState();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pageNoState, setPageNoState] = useState(1);
  const [contents, setContents] = useState<ContentSearchItemType[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [modalCreateContentOpen, setModalCreateContentOpen] = useState(false);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * コンテンツ情報を検索する。
   */
  const search = async (pageNoStr: string | undefined) => {
    const no = parsePageNo(pageNoStr);

    // ローディングの表示
    setProcessing(true);

    // コンテンツ検索 API の呼び出し
    const response = await postContentSearch({
      companyId: acctState.company.id,
      limit: PAGE_LIMIT,
      offset: computeOffset(no, PAGE_LIMIT),
    });

    // 検索結果の設定
    setContents(response.contents);
    setPageNoState(no);
    setHasNext(response.hasNext);

    // ローディングの停止
    setProcessing(false);
  };

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(
    () => {
      // pageNoParam が変わるたびに検索を実行する。
      // search は useCallback 化されていないため render ごとに参照が変わり、
      // 依存に含めると毎 render 発火する。pageNoParam の変化のみで発火する
      // 移行元の挙動を保持するため意図的に除外している。
      (async () => {
        try {
          await search(pageNoParam);
        } catch (e: unknown) {
          showBoundary(e);
        } finally {
          setInitialized(true);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageNoParam]
  );

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * ページングの前へボタン押下時の処理
   */
  const onPrev = () => {
    if (pageNoState <= 1) {
      return;
    }
    navigate(buildContentSearchPageUrl(acctState.company.cd, pageNoState - 1));
  };

  /**
   * ページングの次へボタン押下時の処理
   */
  const onNext = () => {
    if (!hasNext) {
      return;
    }
    navigate(buildContentSearchPageUrl(acctState.company.cd, pageNoState + 1));
  };

  /**
   * 詳細ボタン押下時の処理
   */
  const onEdit = (content: ContentSearchItemType) => {
    navigate(
      `/ws/${acctState.company.cd}/${getContentsTypeStr(
        content.contentsTypeDiv
      )}/edit/${content.cd}`
    );
  };

  /**
   * レポートボタン押下時の処理
   */
  const onReport = (content: ContentSearchItemType) => {
    navigate(
      `/ws/${acctState.company.cd}/report/${getContentsTypeStr(
        content.contentsTypeDiv
      )}/${content.cd}`
    );
  };

  /**
   * 複製ボタン押下時の処理
   */
  const onCopy = (content: ContentSearchItemType) => {
    confirm({
      title: `${getContentsTypeStrJpn(content.contentsTypeDiv)}の複製`,
      message: `「${content.title}」を複製してもよろしいですか？`,
      onSubmit: async () => {
        try {
          // ボタンローディングの表示
          setProcessing(true);
          // contentsTypeDiv に応じた copy API の呼び出し
          await dispatchContentCopy(content, {
            postQuizCopy,
            postDiagnosisCopy,
            postFormCopy,
            postVoiceChatCopy,
          });
          // 完了メッセージの表示
          noticeMessageSuccess(
            `${getContentsTypeStrJpn(content.contentsTypeDiv)}の複製が完了しました。`
          );
        } catch (e: unknown) {
          showBoundary(e);
        } finally {
          setProcessing(false);
          // 一覧を再取得
          await search(pageNoParam);
        }
      },
    });
  };

  /**
   * コンテンツ作成モーダルを開く
   */
  const onModalCreateContentOpen = () => {
    setModalCreateContentOpen(true);
  };

  return {
    initialized,
    processing,
    pageNoState,
    contents,
    hasNext,
    modalCreateContentOpen,
    setModalCreateContentOpen,
    onModalCreateContentOpen,
    onPrev,
    onNext,
    onEdit,
    onReport,
    onCopy,
  };
}
